import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Activity, AlertTriangle, Battery, Check, CheckCircle2, Clock3, Copy, Gauge,
  Monitor, Pencil, Smartphone, TimerReset, Wifi, WifiOff,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { formatDate } from '@meno/ui/lib/utils'
import { screenDisplayStatus } from '@meno/ui/components/shared/ScreenStatusBadge'
import { SectionLabel, SectionSurface, SECTION_STACK } from './settings-primitives'
import { autoLaunchState } from './DeviceTelemetryPanel'

type OverviewScreen = {
  public_id: string
  name: string
  created_at?: string
  status: string
  effective_status?: string
  is_active?: boolean
  device_kind?: 'android' | 'web'
  device_model?: string
  os_version?: string
  app_version?: string
  app_version_code?: number | null
  app_channel?: string | null
  resolution?: string
  last_seen?: string
  last_synced_at?: string | null
  device_boot_at?: string | null
  battery_pct?: number | null
  battery_charging?: boolean | null
  timezone?: string | null
  auto_launch_permission?: boolean | null
  auto_launch_enabled?: boolean | null
  is_home_app?: boolean | null
  disconnect_at?: string | null
}

type OverviewLog = {
  id: number
  event_type: string
  message?: string
  created_at: string
}

export default function OverviewColumn({
  screen, logs, orientation, rotation, scaling, currentGroupName,
  onRename, onViewActivity,
}: {
  screen: OverviewScreen
  logs: OverviewLog[]
  orientation: 'horizontal' | 'vertical'
  rotation: 0 | 90 | 180 | 270
  scaling: 'default' | 'fit'
  currentGroupName: string | null
  onRename: (name: string) => void
  onViewActivity: () => void
}) {
  const { t, i18n } = useTranslation()
  const [editingName, setEditingName] = useState(false)
  const [copied, setCopied] = useState(false)
  const [nameDraft, setNameDraft] = useState(screen.name)
  const status = screenDisplayStatus(screen)
  const online = status === 'online'
  const uptime = screen.device_boot_at ? compactUptime(screen.device_boot_at) : null
  // Two, not five. Overview is a summary — the full history is one click away
  // behind "View all", and a five-row log was the tallest block on the tab for
  // the least summary-shaped content on it.
  const recent = [...logs]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 2)
  const issues = [
    status === 'offline' ? t('screens.overviewOffline', 'This screen is offline. Published changes will sync when it reconnects.') : null,
    status === 'expired' ? t('screens.overviewExpired', 'The screen license has expired and playback is interrupted.') : null,
    autoLaunchState(screen) === 'off' ? t('screens.autoLaunchOffIssue', 'Relaunch on boot is switched off on the device.') : null,
    autoLaunchState(screen) === 'no-permission' ? t('screens.autoLaunchMissingIssue', 'Relaunch on boot needs “Display over other apps”.') : null,
    screen.is_home_app === false ? t('screens.isHomeAppNo', 'Home app is not configured; the screen can be interrupted.') : null,
  ].filter(Boolean) as string[]

  return (
    <div className={SECTION_STACK}>
      <SectionSurface className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-muted/50">
            {screen.device_kind === 'web'
              ? <Monitor className="h-5 w-5 text-muted-foreground" />
              : <Smartphone className="h-5 w-5 text-muted-foreground" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {editingName ? (
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={event => setNameDraft(event.target.value)}
                  onBlur={() => {
                    const next = nameDraft.trim()
                    if (next) onRename(next)
                    else setNameDraft(screen.name)
                    setEditingName(false)
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter') event.currentTarget.blur()
                    if (event.key === 'Escape') { setNameDraft(screen.name); setEditingName(false) }
                  }}
                  maxLength={150}
                  className="min-w-0 flex-1 border-b border-primary bg-transparent text-base font-semibold outline-none"
                />
              ) : (
                <>
                  <h2 className="truncate text-base font-semibold">{screen.name}</h2>
                  <button type="button" onClick={() => { setNameDraft(screen.name); setEditingName(true) }} className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" aria-label={t('common.rename', 'Rename')}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
              {/* Status sits WITH the name, not in a labelled cell below it. It
                  is the first thing anyone opens this page to check, and an
                  eyebrow reading "STATUS" over the word "Online" spent a whole
                  row saying what the green dot already says. */}
              <span className={`ms-auto inline-flex shrink-0 items-center gap-1.5 text-xs font-medium ${online ? 'text-success-fg' : status === 'expired' ? 'text-destructive' : 'text-amber-600 dark:text-amber-400'}`}>
                <span className="h-2 w-2 rounded-full bg-current" />
                {t(`common.${status}`, status)}
              </span>
            </div>
            {/* One line, not two. Make and OS are a single answer to "what is
                this device" — stacked as separate muted lines they read as two
                unrelated facts and pushed everything below them down. */}
            <p className="truncate text-xs text-muted-foreground">
              {screen.device_model || t('screens.deviceNotReported', 'Device model not reported')}
              {' · '}
              {screen.os_version || (screen.device_kind === 'web' ? 'Web' : 'Android')}
            </p>

            {/* Added / Last seen / Screen ID as one ruled row of label-over-
                value pairs. These were three different shapes before — a bare
                sentence, a fragment trailing the status, and a right-aligned
                cell an entire column away from its own eyebrow — so nothing
                lined up and the ID was cut off with no way to read the rest.
                It is copyable now, which is the only thing anyone does with it. */}
            <dl className="mt-3 grid gap-x-4 gap-y-3 border-t border-[hsl(var(--border-control))] pt-3 sm:grid-cols-3">
              <Fact label={t('screens.addedOn', 'Added on')}>
                {screen.created_at ? formatDate(screen.created_at, i18n.language) : '—'}
              </Fact>
              <Fact label={t('screens.lastSeen', 'Last seen')}>
                {screen.last_seen ? formatDate(screen.last_seen, i18n.language) : t('screens.neverConnected', 'Never connected')}
              </Fact>
              <Fact label={t('screens.screenId', 'Screen ID')}>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(screen.public_id)
                    setCopied(true)
                    window.setTimeout(() => setCopied(false), 1500)
                  }}
                  title={screen.public_id}
                  aria-label={`${t('common.copy', 'Copy')} ${t('screens.screenId', 'Screen ID')}`}
                  className="flex w-full min-w-0 items-center gap-1.5 rounded-md text-start text-muted-foreground transition-colors hover:text-foreground"
                >
                  <span className="truncate font-mono text-xs text-foreground/80">{screen.public_id}</span>
                  {copied
                    ? <Check className="h-3.5 w-3.5 shrink-0 text-success-fg" />
                    : <Copy className="h-3.5 w-3.5 shrink-0" />}
                </button>
              </Fact>
            </dl>
          </div>
        </div>
      </SectionSurface>

      <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
        <Metric icon={Battery} label={t('screens.battery', 'Battery')} value={screen.battery_pct == null ? t('common.notAvailable', 'Not available') : `${screen.battery_pct}%`} detail={screen.battery_charging ? t('screens.charging', 'Charging') : undefined} />
        <Metric icon={TimerReset} label={t('screens.uptime', 'Uptime')} value={uptime || t('common.notAvailable', 'Not available')} />
        <Metric icon={Wifi} label={t('screens.lastSync', 'Last sync')} value={screen.last_synced_at ? formatDate(screen.last_synced_at, i18n.language) : t('common.notAvailable', 'Not available')} />
        <Metric icon={Clock3} label={t('screens.timezone', 'Timezone')} value={screen.timezone || t('common.notAvailable', 'Not available')} />
      </div>

      {/* Current configuration reads across, not down. These are five short
          facts an operator scans for a mismatch ("why is this one vertical?"),
          and a stacked row list gave each one a full page-width line whose
          label and value sat at opposite ends — the eye had to travel the
          whole width per fact. Laid out as tiles they sit in one glance, and
          they match the metric tiles directly above instead of introducing a
          second shape for the same kind of content. */}
      <section>
        <SectionLabel>{t('screens.currentConfiguration', 'Current configuration')}</SectionLabel>
        <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
          <ConfigTile icon={Monitor} label={t('screens.orientation', 'Orientation')} value={`${t(`screens.${orientation === 'horizontal' ? 'orientHorizontal' : 'orientVertical'}`, orientation)} · ${rotation}°`} />
          <ConfigTile icon={Gauge} label={t('screens.resolution', 'Resolution')} value={screen.resolution || t('common.notAvailable', 'Not available')} />
          <ConfigTile icon={Activity} label={t('screens.scaling', 'Content scaling')} value={scaling === 'fit' ? t('screens.scalingFill', 'Fill') : t('screens.scalingFit', 'Fit')} />
          <ConfigTile icon={Smartphone} label={t('screens.appVersion', 'App version')} value={screen.app_version ? `${screen.app_version}${screen.app_version_code != null ? ` · #${screen.app_version_code}` : ''}` : t('common.notAvailable', 'Not available')} />
          {currentGroupName && <ConfigTile icon={CheckCircle2} label={t('screens.assignedGroup', 'Assigned group')} value={currentGroupName} />}
        </div>
      </section>

      {issues.length > 0 && (
        <section>
          <SectionLabel>{t('screens.attentionRequired', 'Attention required')}</SectionLabel>
          <div className="space-y-2">
            {issues.map((issue, index) => (
              <div key={issue} className={`flex gap-3 rounded-xl border p-3 ${status === 'expired' && index === 0 ? 'border-destructive/30 bg-destructive/5' : 'border-amber-500/25 bg-amber-500/5'}`}>
                {status === 'offline' && index === 0 ? <WifiOff className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />}
                <p className="text-xs leading-relaxed">{issue}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="mb-1.5 flex items-center justify-between">
          <SectionLabel>{t('screens.recentActivity', 'Recent activity')}</SectionLabel>
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onViewActivity}>{t('screens.viewAll', 'View all')}</Button>
        </div>
        <SectionSurface className="divide-y divide-border overflow-hidden">
          {recent.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">{t('screens.noRecentActivity', 'No recent activity')}</p>
          ) : recent.map(log => (
            <div key={log.id} className="flex items-start gap-3 px-4 py-3">
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-muted-foreground/60" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{humanizeEvent(log.event_type)}</p>
                {log.message && <p className="truncate text-xs text-muted-foreground">{log.message}</p>}
              </div>
              <time className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{new Date(log.created_at).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}</time>
            </div>
          ))}
        </SectionSurface>
      </section>
    </div>
  )
}

function Metric({ icon: Icon, label, value, detail }: { icon: typeof Battery; label: string; value: string; detail?: string }) {
  return (
    <div className="min-w-0 rounded-xl bg-muted/35 p-3">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</div>
      <p className="mt-2 truncate text-sm font-semibold" title={value}>{value}</p>
      {detail && <p className="mt-0.5 text-[11px] text-muted-foreground">{detail}</p>}
    </div>
  )
}

/** One label-over-value pair in the identity card's fact row. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-1 truncate text-xs text-foreground/80">{children}</dd>
    </div>
  )
}

function ConfigTile({ icon: Icon, label, value }: { icon: typeof Monitor; label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-xl bg-muted/35 p-3">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Icon className="h-3.5 w-3.5 shrink-0" />{label}</div>
      <p className="mt-2 truncate text-sm font-semibold" title={value}>{value}</p>
    </div>
  )
}

function compactUptime(bootAt: string): string | null {
  const ms = Date.now() - new Date(bootAt).getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  const hours = Math.floor(ms / 3_600_000)
  const days = Math.floor(hours / 24)
  return days > 0 ? `${days}d ${hours % 24}h` : `${hours}h ${Math.floor((ms % 3_600_000) / 60_000)}m`
}

function humanizeEvent(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}
